/**
 * Reine Regeln der Bauphase — „der Admin kuendigt Wartung an, alle sehen den
 * Countdown, ab `locksAt` steht die Wartungsseite, bei `expiresAt` ist von
 * selbst Schluss".
 *
 * Bewusst ohne Supabase-Import (nur die reine `isNewerBuild` aus dem
 * Version-Gate), ohne Seiteneffekte und ohne eigenen
 * Zeitzugriff: die Zeit kommt immer als Parameter herein — dieselbe Trennung
 * wie bei projectLockLogic/projectLockService (das Projekt hat kein jsdom). Das
 * Reden mit der Datenbank steht in maintenanceService.ts, die Zustandsfuehrung
 * im Hook useMaintenance, die Anzeige in der Oberflaeche.
 *
 * Grundhaltung fuer alle Randfaelle: FAIL-OPEN. Eine kaputte, unlesbare oder
 * fehlende Zeile darf niemanden aussperren — der eigentliche Riegel sitzt
 * ohnehin serverseitig (Trigger auf `projects` wirft 'BAUPHASE'); die Anzeige
 * hier ist Hoeflichkeit, kein Schutz.
 *
 * Alle Zeitvergleiche laufen gegen `nowMs`, das der Aufrufer um den
 * Server-Versatz korrigiert hereinreicht (`clockOffsetMs`). Eine nachgehende
 * Rechneruhr wuerde sonst den Countdown zeigen, waehrend der Server laengst
 * jeden Save abweist.
 */

import { isNewerBuild } from './versionGate';

export interface MaintenanceRow {
    active: boolean;
    /** Freitext des Admins fuer die Wartungsseite; leer erlaubt. */
    message: string;
    /** Wann angekuendigt wurde (ISO-8601). */
    announcedAt: string | null;
    /** Ab wann die App zu ist (ISO-8601). */
    locksAt: string | null;
    /** Ab wann sie ohne Zutun wieder auf ist (ISO-8601). */
    expiresAt: string | null;
    /** Wer angekuendigt hat (auth.uid); null = unbekannt. */
    startedBy: string | null;
    /**
     * Build-Zeitstempel (ISO-8601), der beim Ankuendigen ausgeliefert war
     * (Nr. 95). Ein Client mit NEUEREM Build meldet „Update ist da" und
     * beendet die Bauphase (maintenance_release). null = unbekannt, dann
     * endet sie nur per Klick oder mit `expiresAt`.
     */
    deployedBuild: string | null;
    /**
     * Mindest-Build des Build-Riegels (ISO-8601, Nr. 112/113) — ein Bundle,
     * dessen `__BUILD_TIME__` davor liegt, darf nicht mehr schreiben.
     *
     * null = Riegel AUS. Das ist auch der Wert, den ein Server ohne Nr. 113
     * liefert (`maintenance_status()` kennt das Feld dann gar nicht) — der
     * Client laeuft damit unveraendert weiter. Der Wert ist UNABHAENGIG von
     * `active`: der Riegel gilt im Normalbetrieb, nicht nur in der Bauphase.
     */
    minBuild: string | null;
}

/**
 * In welcher Lage die App gerade ist.
 *
 * 'none'      = keine Bauphase (auch: nicht lesbar, ausgelaufen, Unsinn).
 * 'announced' = angekuendigt, noch mehr als MAINTENANCE_WARNING_MS Zeit.
 * 'warning'   = die letzten zwei Minuten — Sekundentakt, jetzt speichern.
 * 'locked'    = zu; Nicht-Admins sehen die Wartungsseite.
 */
export type MaintenancePhase = 'none' | 'announced' | 'warning' | 'locked';

export interface MaintenanceState {
    phase: MaintenancePhase;
    /**
     * Restzeit in ms — bis zum Zusperren ('announced'/'warning') bzw. bis zum
     * automatischen Ende ('locked'). 0, wenn keine bekannt.
     */
    remainingMs: number;
    /**
     * Wann wieder nachsehen (ms).
     *
     * Der Zeitgeber plant sich damit selbst neu, statt im Sekundentakt zu
     * laufen: vor der Warnphase schlaeft er bis kurz vor deren Beginn, in ihr
     * tickt er sekundengenau, im gesperrten Zustand alle fuenf Sekunden.
     * Gedeckelt, damit die Anzeige nach einem Standby-Sprung nicht minutenlang
     * falsch steht (Muster describeIdleCountdown).
     */
    nextCheckMs: number;
    /** Gesperrt, aber der Betrachter ist Admin — er darf hinein und beenden. */
    adminBypass: boolean;
    locksAtMs: number | null;
    expiresAtMs: number | null;
    message: string;
    /**
     * `row.minBuild`, unveraendert durchgereicht (Build-Riegel).
     *
     * Er geht durch die PHASENLOGIK hindurch, ohne sie zu beruehren: der
     * Riegel gilt auch ohne Bauphase, und eine laufende Bauphase sagt
     * nichts ueber ihn. Er steht hier nur, damit die Anzeige ihn ohne einen
     * zweiten Datenweg hat.
     */
    minBuild: string | null;
}

/**
 * Vorlauf der Sekundenwarnung vor dem Zusperren.
 *
 * Zwei Minuten reichen, um einen offenen Bericht zu speichern; laenger
 * tickte die Uhr nur nervoes im Bild, ohne dass jemand etwas tun koennte.
 */
export const MAINTENANCE_WARNING_MS = 2 * 60 * 1000;

/** Vorbelegung des Admin-Dialogs: Zeit von Ankuendigung bis Zusperren. */
export const MAINTENANCE_DEFAULT_MINUTES = 15;

/**
 * Fensterlaenge — wie lange die App nach dem Zusperren hoechstens zu bleibt.
 *
 * Vorbelegung und Grenzen spiegeln die Klemme in `maintenance_start`
 * (Nr. 96: 5..480, null → 60). Vergisst der Administrator das Beenden,
 * oeffnet die App nach Ablauf von selbst; ist das Update frueher da, endet
 * sie frueher (siehe shouldReleaseForBuild).
 */
export const MAINTENANCE_DEFAULT_DURATION_MINUTES = 60;
export const MAINTENANCE_MIN_DURATION_MINUTES = 5;
export const MAINTENANCE_MAX_DURATION_MINUTES = 480;

/** Wie oft in 'announced' hoechstens nachgerechnet wird. */
const ANNOUNCED_MAX_CHECK_MS = 30_000;

/** Untergrenze jedes Zeitgebers — kein Dauerfeuer bei Grenzwerten. */
const MIN_CHECK_MS = 250;

/** Im gesperrten Zustand: knapper Blick, ob der Admin schon beendet hat. */
const LOCKED_CHECK_MS = 5_000;

const NONE_STATE: MaintenanceState = {
    phase: 'none',
    remainingMs: 0,
    nextCheckMs: ANNOUNCED_MAX_CHECK_MS,
    adminBypass: false,
    locksAtMs: null,
    expiresAtMs: null,
    message: '',
    minBuild: null,
};

/**
 * Der 'none'-Zustand mit durchgereichtem Mindest-Build.
 *
 * Ohne Riegel (der Normalfall bis Nr. 113 laeuft) kommt DASSELBE Objekt
 * zurueck wie bisher — die Zustandsfuehrung im Hook erkennt daran „nichts
 * Neues" und rendert nicht neu. Mit Riegel entsteht ein neues Objekt; dass
 * das keinen Renderregen ausloest, besorgt `istGleicherMaintenanceStand`.
 */
const noneState = (minBuild: string | null): MaintenanceState =>
    minBuild === null ? NONE_STATE : { ...NONE_STATE, minBuild };

/**
 * ISO-Zeitstempel in Millisekunden — oder null, wenn er unbrauchbar ist.
 *
 * `Date.parse` liefert bei Unsinn NaN und NICHT etwa 0; ohne diese Pruefung
 * laege `locksAt` am 1.1.1970 und die App waere sofort und fuer immer zu.
 */
const parseTimestamp = (value: unknown): number | null => {
    if (typeof value !== 'string' || value === '') return null;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
};

/** Admin oder Super-Admin — dieselbe Regel wie isAdminRole in projectLockLogic. */
const isAdminRole = (role: string | null | undefined): boolean =>
    role === 'admin' || role === 'super_admin';

/** Bis zur naechsten vollen Sekunde — damit die Anzeige nicht springt. */
const untilNextSecond = (nowMs: number): number => {
    const rest = 1_000 - (nowMs % 1_000);
    return rest < MIN_CHECK_MS ? rest + 1_000 : rest;
};

/**
 * Stand der Dinge fuer Anzeige UND Zeitgeber in einem Aufruf.
 *
 * Randfaelle, alle in Richtung „offen":
 *   * keine Zeile, `active=false`, unlesbares `locksAt` → 'none'.
 *   * `expiresAt` fehlt oder ist unlesbar → die Sperre gilt, bis der Admin sie
 *     beendet (remainingMs 0). Das ist der einzige Fall, der NICHT fail-open
 *     ist — bewusst: die Zeile sagt ausdruecklich „zu", nur das Ende ist
 *     unbekannt, und der Server weist Saves ohnehin ab.
 *   * `now >= expiresAt` → 'none', auch wenn `active` noch true steht; die
 *     RPC liefert in dem Fall zwar schon active=false, aber zwischen zwei
 *     Abgleichen laeuft die Uhr hier weiter.
 */
export const describeMaintenance = (args: {
    row: MaintenanceRow | null;
    nowMs: number;
    role?: string | null;
}): MaintenanceState => {
    const { row, nowMs, role } = args;
    // Der Mindest-Build gehoert NICHT zur Phase: er gilt auch ohne Bauphase
    // und wird durch jeden Zweig unveraendert durchgereicht.
    const minBuild = typeof row?.minBuild === 'string' && row.minBuild !== '' ? row.minBuild : null;
    if (!row || !row.active) return noneState(minBuild);

    const locksAtMs = parseTimestamp(row.locksAt);
    if (locksAtMs === null) return noneState(minBuild);

    const expiresAtMs = parseTimestamp(row.expiresAt);
    const message = typeof row.message === 'string' ? row.message : '';

    if (expiresAtMs !== null && nowMs >= expiresAtMs) return noneState(minBuild);

    const untilLock = locksAtMs - nowMs;

    if (untilLock > MAINTENANCE_WARNING_MS) {
        // Bis kurz vor die Warnphase schlafen, hoechstens aber 30 s: Realtime
        // kann ausfallen, und ein Standby-Sprung soll die Anzeige nicht
        // minutenlang stehen lassen.
        const bisWarnung = untilLock - MAINTENANCE_WARNING_MS;
        return {
            phase: 'announced',
            remainingMs: untilLock,
            nextCheckMs: Math.max(MIN_CHECK_MS, Math.min(ANNOUNCED_MAX_CHECK_MS, bisWarnung)),
            adminBypass: false,
            locksAtMs,
            expiresAtMs,
            message,
            minBuild,
        };
    }

    if (untilLock > 0) {
        return {
            phase: 'warning',
            remainingMs: untilLock,
            // Sekundentakt, aber nicht ueber den Sperrzeitpunkt hinaus schlafen.
            nextCheckMs: Math.max(MIN_CHECK_MS, Math.min(untilNextSecond(nowMs), untilLock)),
            adminBypass: false,
            locksAtMs,
            expiresAtMs,
            message,
            minBuild,
        };
    }

    return {
        phase: 'locked',
        remainingMs: expiresAtMs === null ? 0 : Math.max(0, expiresAtMs - nowMs),
        nextCheckMs: LOCKED_CHECK_MS,
        adminBypass: isAdminRole(role),
        locksAtMs,
        expiresAtMs,
        message,
        minBuild,
    };
};

/**
 * Restzeit als „mm:ss".
 *
 * Aufgerundet, damit die Anzeige nicht bei „00:00" haengt, solange noch eine
 * halbe Sekunde laeuft — und geklemmt, damit sie unter keinen Umstaenden eine
 * negative Zeit zeigt. Ueber 99 Minuten wachsen die Minuten einfach weiter;
 * eine Stundenanzeige braucht die Bauphase nicht.
 */
export const formatMaintenanceCountdown = (ms: number): string => {
    const total = Math.max(0, Math.ceil((Number.isFinite(ms) ? ms : 0) / 1_000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** Das Signalwort, das der Trigger in supabase/app_maintenance.sql wirft. */
export const MAINTENANCE_ERROR_MARKER = 'BAUPHASE';

const containsMarker = (value: unknown): boolean =>
    typeof value === 'string' && value.includes(MAINTENANCE_ERROR_MARKER);

/**
 * Hat der Server einen Schreibversuch wegen der Bauphase abgewiesen?
 *
 * Postgres verpackt `raise exception 'BAUPHASE …'` je nach Weg verschieden:
 * bei supabase-js steht der Text in `message`, bei manchen Fehlerhuellen in
 * `details` oder `hint`, und wer den Fehler weiterwirft, macht daraus ein
 * `Error` mit dem Text in `.message`. Alle vier Stellen werden abgesucht;
 * gesucht wird per „enthaelt", weil PostgREST dem Text gelegentlich etwas
 * voranstellt.
 */
export const isMaintenanceWriteError = (err: unknown): boolean => {
    if (err === null || err === undefined) return false;
    if (typeof err === 'string') return containsMarker(err);
    if (typeof err !== 'object') return false;
    const e = err as Record<string, unknown>;
    return containsMarker(e.message) || containsMarker(e.details) || containsMarker(e.hint) || containsMarker(e.code);
};

/**
 * jsonb der drei RPCs → Row (snake_case → camelCase).
 *
 * null bei allem, was keine Zeile ist: fehlendes Objekt, Array, Zeichenkette,
 * fehlendes `active`. Ein null landet beim Aufrufer als 'none' — fail-open.
 * Fehlende Zeitstempel werden zu null, nicht zu leeren Strings, damit
 * `describeMaintenance` sie sauber als „unbekannt" liest.
 */
export const rowFromRpc = (payload: unknown): MaintenanceRow | null => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const p = payload as Record<string, unknown>;
    if (typeof p.active !== 'boolean') return null;
    const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
    return {
        active: p.active,
        message: typeof p.message === 'string' ? p.message : '',
        announcedAt: str(p.announced_at),
        locksAt: str(p.locks_at),
        expiresAt: str(p.expires_at),
        startedBy: str(p.started_by),
        deployedBuild: str(p.deployed_build),
        // Fehlt das Feld (Server ohne Nr. 113), bleibt es null — Riegel aus.
        minBuild: str(p.min_build),
    };
};

/**
 * Liegt dieses Bundle UNTER dem Mindest-Build des Riegels?
 *
 * Genau dieselbe Frage, die der Trigger auf dem Server stellt — nur hier,
 * damit der Client es merkt, BEVOR er schreibt, und den Update-Balken zeigt
 * statt eines abgewiesenen Speicherversuchs.
 *
 * FAIL-OPEN in jeder Unklarheit: kein Mindest-Build (Riegel aus, Server ohne
 * Nr. 113), kein eigener Build, unlesbare Werte → false. Gleichstand ist
 * ERLAUBT: der Admin setzt den Riegel auf den ausgelieferten Build, und
 * genau dieser Build muss weiterarbeiten duerfen.
 *
 * Bewusst `isNewerBuild` aus dem Version-Gate — „neuer" heisst im ganzen
 * Projekt dasselbe.
 */
export const istUnterMindestBuild = (
    buildTime: string | null | undefined,
    minBuild: string | null | undefined,
): boolean => isNewerBuild(buildTime ?? undefined, minBuild ?? undefined);

/**
 * Soll der Mindest-Build JETZT gemeldet werden?
 *
 * Drei Dinge muessen zusammenkommen: es gibt einen Mindest-Build, das eigene
 * Bundle liegt darunter, und fuer genau diesen Wert wurde noch nicht
 * gemeldet. Der dritte Teil ist der Grund, warum das hier eine eigene Regel
 * ist und nicht zwei Zeilen im Hook: der Abgleich laeuft alle 30 Sekunden,
 * und ohne den Merker klopfte die Meldung im Halbminutentakt an.
 *
 * `bereitsGemeldetFuer` haelt den WERT, nicht ein ja/nein: zieht der
 * Administrator den Riegel nach (erst Montag, dann Dienstag), ist das eine
 * neue Lage und muss erneut gemeldet werden. Ein blosses Flag verschluckte
 * die zweite Stufe.
 */
export const sollMindestBuildMelden = (
    eigenerBuild: string | null | undefined,
    mindestBuild: string | null | undefined,
    bereitsGemeldetFuer: string | null,
): boolean => {
    if (!istUnterMindestBuild(eigenerBuild, mindestBuild)) return false;
    return bereitsGemeldetFuer !== (mindestBuild ?? null);
};

/**
 * Ein Build-Zeitstempel fuer den Bildschirm: Datum und Uhrzeit, lokale Zeit.
 *
 * `—` bei allem, was kein lesbarer Zeitpunkt ist. Ein unlesbarer Stempel
 * darf nicht als „gesetzt" durchgehen — der Administrator entscheidet
 * anhand dieser Zeile, ob er die Flotte aussperrt.
 *
 * Steht hier und nicht in der Oberflaeche, damit er ohne DOM pruefbar ist
 * (das Projekt hat kein jsdom) und damit die Komponente nichts exportiert,
 * was keine Komponente ist.
 */
export const formatBuildStempel = (iso: string | null | undefined): string => {
    if (typeof iso !== 'string' || iso === '') return '—';
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return '—';
    return new Date(ms).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

/**
 * Sind zwei gelesene Zeilen gleichwertig?
 *
 * Wie `istGleicherMaintenanceStand`, nur fuer die Rohzeile: `fetchMaintenance`
 * baut bei JEDEM Abgleich ein frisches Objekt, auch wenn sich an der einen
 * Zeile in der Datenbank seit Tagen nichts geruehrt hat. Wuerde der Hook das
 * ungeprueft in den Zustand legen, renderte alles, was an `row` haengt, alle
 * 30 Sekunden neu — der Renderregen, den der Vergleich der Staende gerade
 * verhindert.
 */
export const istGleicheMaintenanceZeile = (
    a: MaintenanceRow | null,
    b: MaintenanceRow | null,
): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.active === b.active
        && a.message === b.message
        && a.announcedAt === b.announcedAt
        && a.locksAt === b.locksAt
        && a.expiresAt === b.expiresAt
        && a.startedBy === b.startedBy
        && a.deployedBuild === b.deployedBuild
        && a.minBuild === b.minBuild;
};

/**
 * Sind zwei gerechnete Staende gleichwertig?
 *
 * `describeMaintenance` ist rein und liefert bei jedem Lauf ein frisches
 * Objekt, sobald ein Mindest-Build gesetzt ist (ohne ihn kommt das
 * eingefrorene NONE_STATE zurueck). Der Zeitgeber im Hook rechnet in der
 * Ruhelage alle 30 s nach — ohne diesen Vergleich rendert die ganze App
 * dann alle 30 s neu, nur weil der Riegel scharf ist.
 */
export const istGleicherMaintenanceStand = (
    a: MaintenanceState,
    b: MaintenanceState,
): boolean =>
    a.phase === b.phase
    && a.remainingMs === b.remainingMs
    && a.nextCheckMs === b.nextCheckMs
    && a.adminBypass === b.adminBypass
    && a.locksAtMs === b.locksAtMs
    && a.expiresAtMs === b.expiresAtMs
    && a.message === b.message
    && a.minBuild === b.minBuild;

/**
 * Darf DIESER Client die laufende Bauphase beenden, weil er das Update ist?
 *
 * Ja genau dann, wenn die Zeile aktiv ist, einen `deployedBuild` kennt und
 * der eigene Build-Zeitstempel echt spaeter liegt. Gleich oder aelter
 * (Rollback, derselbe Build in einem frischen Tab) → nein; unlesbare Werte
 * → nein. Dieselbe Regel wie `isNewerBuild` im Version-Gate — bewusst
 * dieselbe Funktion, damit „neuer" hier und dort dasselbe heisst.
 */
export const shouldReleaseForBuild = (
    row: MaintenanceRow | null,
    ownBuild: string | null | undefined,
): boolean => {
    if (!row || !row.active) return false;
    return isNewerBuild(row.deployedBuild ?? undefined, ownBuild);
};

/**
 * Welcher Build-Zeitstempel beim Ankuendigen in die Zeile soll.
 *
 * Der Admin-Tab kann selbst veraltet sein (seit Tagen offen, inzwischen
 * wurde ausgeliefert). Truege die Zeile SEINEN Build, saehe jeder Client mit
 * dem laengst ausgelieferten Stand sofort „neuer" und beendete die Bauphase
 * im selben Augenblick. Deshalb liest der Aufrufer /version.json frisch und
 * es gilt der SPAETERE der beiden Zeitstempel; unlesbare fallen weg, sind
 * beide unlesbar, bleibt es bei null (Ende dann nur per Klick oder Fenster).
 */
export const deployedBuildForStart = (
    ownBuild: string | null | undefined,
    fetchedBuild: string | null | undefined,
): string | null => {
    const own = parseTimestamp(ownBuild);
    const fetched = parseTimestamp(fetchedBuild);
    if (own === null && fetched === null) return null;
    if (own === null) return fetchedBuild as string;
    if (fetched === null) return ownBuild as string;
    return fetched > own ? (fetchedBuild as string) : (ownBuild as string);
};

/**
 * Versatz Server minus Client in ms.
 *
 * Positiv = der Server ist voraus. Der Aufrufer rechnet `Date.now() + offset`
 * und vergleicht damit gegen `locksAt`; so stimmt der Countdown auch auf
 * einem Rechner, dessen Uhr eine Minute nachgeht. 0 bei Unsinn — eine
 * unlesbare Serverzeit soll die Anzeige nicht verbiegen.
 */
export const clockOffsetMs = (
    serverNowIso: string | null | undefined,
    clientNowMs: number,
): number => {
    const serverMs = parseTimestamp(serverNowIso);
    if (serverMs === null || !Number.isFinite(clientNowMs)) return 0;
    return serverMs - clientNowMs;
};
